import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CreditCard, Euro } from 'lucide-react';
import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';
import { InvoiceCreditNoteApplication, InvoiceDownPaymentApplication } from '@/types/invoicing';
import { formatCurrency } from '@/utils/document';

interface ApplicationHistoryProps {
  invoiceId: number;
}

export function ApplicationHistory({ invoiceId }: ApplicationHistoryProps) {
  const [creditNoteApplications, setCreditNoteApplications] = useState<InvoiceCreditNoteApplication[]>([]);
  const [downPaymentApplications, setDownPaymentApplications] = useState<InvoiceDownPaymentApplication[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchApplicationHistory();
  }, [invoiceId]);

  const fetchApplicationHistory = async () => {
    setLoading(true);
    try {
      // Fetch credit note applications
      const creditNoteResponse = await api.get(
        `${ROUTES.API.INVOICE_CREDIT_NOTE_APPLICATIONS}?invoice=${invoiceId}`
      );
      setCreditNoteApplications(creditNoteResponse.data.results || creditNoteResponse.data);

      // Fetch down payment applications
      const downPaymentResponse = await api.get(
        `${ROUTES.API.INVOICE_DOWN_PAYMENT_APPLICATIONS}?invoice=${invoiceId}`
      );
      setDownPaymentApplications(downPaymentResponse.data.results || downPaymentResponse.data);
    } catch (error) {
      console.error('Failed to fetch application history:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalAppliedFromCreditNotes = creditNoteApplications.reduce(
    (sum, app) => sum + (Number(app.amount_applied) || 0), 
    0
  );

  const totalAppliedFromDownPayments = downPaymentApplications.reduce(
    (sum, app) => sum + (Number(app.amount_applied) || 0), 
    0
  );

  const totalApplied = totalAppliedFromCreditNotes + totalAppliedFromDownPayments;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Application History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">Loading application history...</div>
        </CardContent>
      </Card>
    );
  }

  if (creditNoteApplications.length === 0 && downPaymentApplications.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Application History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-muted-foreground">
            No credit notes or down payments have been applied to this invoice.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Euro className="h-5 w-5" />
          Application History
        </CardTitle>
        <div className="text-sm text-muted-foreground">
          Total Applied: <span className="font-medium">{formatCurrency(totalApplied)}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Credit Note Applications */}
        {creditNoteApplications.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="h-4 w-4" />
              <h4 className="font-medium">Credit Note Applications</h4>
              <Badge variant="outline">
                {formatCurrency(totalAppliedFromCreditNotes)}
              </Badge>
            </div>
            <div className="space-y-2">
              {creditNoteApplications.map((application) => (
                <div
                  key={application.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium">Credit Note #{application.credit_note}</div>
                    <div className="text-sm text-muted-foreground">
                      Applied on {new Date(application.applied_at).toLocaleDateString()}
                    </div>
                    {application.notes && (
                      <div className="text-sm text-muted-foreground mt-1">
                        Note: {application.notes}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-medium">
                      {formatCurrency(Number(application.amount_applied) || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Applied by User #{application.applied_by}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Separator if both types exist */}
        {creditNoteApplications.length > 0 && downPaymentApplications.length > 0 && (
          <Separator />
        )}

        {/* Down Payment Applications */}
        {downPaymentApplications.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Euro className="h-4 w-4" />
              <h4 className="font-medium">Down Payment Applications</h4>
              <Badge variant="outline">
                {formatCurrency(totalAppliedFromDownPayments)}
              </Badge>
            </div>
            <div className="space-y-2">
              {downPaymentApplications.map((application) => (
                <div
                  key={application.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium">Down Payment #{application.down_payment}</div>
                    <div className="text-sm text-muted-foreground">
                      Applied on {new Date(application.applied_at).toLocaleDateString()}
                    </div>
                    {application.notes && (
                      <div className="text-sm text-muted-foreground mt-1">
                        Note: {application.notes}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-medium">
                      {formatCurrency(Number(application.amount_applied) || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Applied by User #{application.applied_by}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
